/** Chart labels follow appearance without replacing datasets, selection, or the canvas. */
(() => {
    'use strict';
    const registered = new WeakSet();
    const originals = new WeakMap();
    let printing = false;

    function palette() {
        const root = document.documentElement;
        if (printing || root.dataset.appearance !== 'active') return null;
        const css = getComputedStyle(root);
        return {
            ink: css.getPropertyValue('--appearance-ink').trim(),
            muted: css.getPropertyValue('--appearance-muted').trim(),
            panel: css.getPropertyValue('--appearance-panel').trim(),
            border: css.getPropertyValue('--appearance-border').trim(),
            family: css.getPropertyValue('--appearance-font').trim(),
            scale: Number(css.getPropertyValue('--appearance-scale')) || 1,
            strong: root.dataset.appearanceWeight === 'strong',
        };
    }

    function recolor(chart) {
        if (!chart.canvas || chart.canvas.closest('[data-appearance-preserve]')) return;
        let saved = originals.get(chart);
        if (!saved) { saved = new Map(); originals.set(chart, saved); }
        function original(target, key) {
            let properties = saved.get(target);
            if (!properties) { properties = new Map(); saved.set(target, properties); }
            if (!properties.has(key)) properties.set(key, {has: Object.hasOwn(target, key), value: target[key]});
            return properties.get(key).value;
        }
        function set(target, key, value) { original(target, key); target[key] = value; }
        const colors = palette();
        if (!colors) {
            saved.forEach((properties, target) => properties.forEach((entry, key) => {
                if (entry.has) target[key] = entry.value;
                else delete target[key];
            }));
            originals.delete(chart);
            return;
        }
        const options = chart.config.options;
        const version = parseInt(chart.constructor.version || window.Chart?.version || '4', 10);
        const object = (target, key) => {
            if (target[key] === false) return null;
            if (!target[key] || typeof target[key] !== 'object') set(target, key, {});
            return target[key];
        };
        function font(target, fallback = 12, defaultWeight = 'normal') {
            if (!target) return;
            if (version < 3) {
                set(target, 'fontFamily', colors.family);
                set(target, 'fontSize', (Number(original(target, 'fontSize')) || fallback) * colors.scale);
                if (colors.strong) set(target, 'fontStyle', 'bold');
                else if (saved.get(target)?.has('fontStyle')) set(target, 'fontStyle', original(target, 'fontStyle'));
                return;
            }
            const previous = original(target, 'font');
            if (typeof previous === 'function') return;
            const value = previous && typeof previous === 'object' ? previous : {};
            set(target, 'font', {...value, family: colors.family, size: (Number(value.size) || fallback) * colors.scale,
                weight: colors.strong ? 600 : (value.weight || defaultWeight)});
        }
        function label(target, color = colors.ink, defaultWeight = 'normal') {
            if (!target) return;
            set(target, version < 3 ? 'fontColor' : 'color', color);
            font(target, 12, defaultWeight);
        }
        function scale(axis) {
            if (!axis || typeof axis !== 'object') return;
            const ticks = object(axis, 'ticks');
            label(ticks, colors.muted);
            if (ticks) set(ticks, 'backdropColor', colors.panel);
            label(object(axis, version < 3 ? 'scaleLabel' : 'title'));
            if (axis.type === 'radialLinear' || chart.config.type === 'radar' || chart.config.type === 'polarArea') label(object(axis, 'pointLabels'));
            const grid = object(axis, version < 3 ? 'gridLines' : 'grid');
            if (grid) set(grid, 'color', colors.border);
            const angle = axis.angleLines;
            if (angle && typeof angle === 'object') set(angle, 'color', colors.border);
        }

        if (version < 3) {
            label(object(object(options, 'legend') || {}, 'labels'));
            label(object(options, 'title'));
            const scales = options.scales || {};
            [...(scales.xAxes || []), ...(scales.yAxes || [])].forEach(scale);
            if (options.scale) scale(options.scale);
            const tip = object(options, 'tooltips');
            if (tip) {
                set(tip, 'backgroundColor', colors.panel);
                ['titleFontColor', 'bodyFontColor', 'footerFontColor'].forEach(key => set(tip, key, colors.ink));
            }
            return;
        }
        set(options, 'color', colors.ink);
        const plugins = object(options, 'plugins');
        if (plugins) {
            const legend = object(plugins, 'legend');
            if (legend) label(object(legend, 'labels'));
            label(object(plugins, 'title'), colors.ink, 'bold');
            label(object(plugins, 'subtitle'));
            const tip = object(plugins, 'tooltip');
            if (tip) {
                set(tip, 'backgroundColor', colors.panel);
                set(tip, 'borderColor', colors.border);
                set(tip, 'borderWidth', 1);
                ['titleColor', 'bodyColor', 'footerColor'].forEach(key => set(tip, key, colors.ink));
                ['titleFont', 'bodyFont', 'footerFont'].forEach(key => {
                    const prior = original(tip, key);
                    if (typeof prior === 'function') return;
                    const value = prior && typeof prior === 'object' ? prior : {};
                    set(tip, key, {...value, family: colors.family, size: (Number(value.size) || 12) * colors.scale,
                        weight: value.weight || (key === 'titleFont' ? 'bold' : 'normal')});
                });
            }
            const annotations = plugins.annotation?.annotations;
            Object.values(annotations || {}).forEach(item => {
                if (!item?.label || typeof item.label !== 'object') return;
                label(item.label);
                set(item.label, 'backgroundColor', colors.panel);
            });
        }
        Object.values(options.scales || {}).forEach(scale);
    }

    function sync() {
        const Chart = window.Chart;
        if (!Chart || (typeof Chart !== 'function' && typeof Chart !== 'object')) return;
        if (!registered.has(Chart)) {
            const plugin = {id: 'skmAppearance', beforeUpdate: recolor};
            if (typeof Chart.register === 'function') Chart.register(plugin);
            else if (Chart.plugins?.register) Chart.plugins.register(plugin);
            else return;
            registered.add(Chart);
        }
        Object.values(Chart.instances || {}).forEach(chart => {
            if (chart.canvas?.closest('[data-appearance-preserve]')) return;
            recolor(chart);
            chart.update(parseInt(Chart.version || '4', 10) < 3 ? 0 : 'none');
        });
    }

    document.addEventListener('skm:appearance-changed', sync);
    document.addEventListener('skm:charts-ready', sync);
    document.addEventListener('DOMContentLoaded', sync, {once: true});
    document.addEventListener('load', event => { if (event.target instanceof HTMLScriptElement) sync(); }, true);
    window.addEventListener('beforeprint', () => { printing = true; sync(); });
    window.addEventListener('afterprint', () => { printing = false; sync(); });
    sync();
})();
